<template>
    <div :class="['table-responsive', {'nested-product-overview-container': nested}]">
        <table class="table b-table table-hover">
            <thead>
                <th>
                    <div style="min-width: 180px;">{{ nameField.label }}</div>
                </th>
                <th v-for="field in activeFields" style="white-space: nowrap;">
                    <div :title="field.title">{{ field.label }}</div>
                </th>
            </thead>
            <tbody v-if="search">
                <tr>
                    <td>
                        <div v-if="nameField.search">
                            <SearchInput
                                :placeholder="nameField.searchPlaceholder ? nameField.searchPlaceholder : undefined"
                                v-if="nameField.searchType === undefined || nameField.searchType === 'input'"
                                @search="(value) => filter(nameField, value)"
                            />
                        </div>
                    </td>
                    <td style="white-space: nowrap;" v-for="field in activeFields">
                        <div v-if="field.search">
                            <SearchInput
                                :placeholder="field.searchPlaceholder ? field.searchPlaceholder : undefined"
                                v-if="field.searchType === undefined || field.searchType === 'input'"
                                @search="(value) => filter(field, value)"
                            />
                            <YesNoSearch v-if="field.searchType === 'YesNo'" @change="(value) => filter(field, value)" />
                        </div>
                    </td>
                </tr>
            </tbody>
            <tbody v-for="product in this.items">
                <tr @click="onRowClick(product)" v-show="!search || (search && !isSearchActive) || (search && isSearchActive && isVisible(product))">
                    <td
                        class="p-0 pl-1"
                        :title="nameRenderer(product)"
                        :style="{'padding-left': (product.level * 20 + 14 + (product.children.length ? 0 : 6 )) + 'px'}"
                    >
                        <div class="d-flex" @click.stop="toggleChildren(product, product.id)">
                            <div
                                v-show="product.children.length > 0"
                                class="pr-50"
                            >
                                <feather-icon :size="iconSize" :icon="isProductChildVisible(product, product.id) ? iconCollapse : iconExpand" />
                            </div>
                            <div>{{ shortNameRenderer(product) }}</div>
                        </div>
                    </td>
                    <slot name="row" :item="product" :activeFields="activeFields">
                        <td v-for="field in activeFields" :class="field.tdClass">
                            <slot :name="'cell-' + field.key" :item="product" :field="field">
                                <div v-html="renderField(field, product)"></div>
                            </slot>
                        </td>
                    </slot>
                </tr>
                <slot
                    name="row_below"
                    :item="product"
                    :isVisible="isVisible(product)"
                    :colCount="activeFields.length + 1"
                />
                <tr
                    v-for="p in product.flatHierarchy"
                    v-if="isProductChildVisible(product, p.parent, p)"
                    @click="onRowClick(p)"
                >
                    <td
                        :title="nameRenderer(p)"
                        :style="{'padding-left': (p.level * 18 + (p.children.length ? 0 : 8 )) + 'px'}"
                    >
                        <div class="d-flex" @click.stop="toggleChildren(product, p.id)">
                            <div
                                v-show="p.children.length > 0"
                                class="pr-50"
                            >
                                <feather-icon :size="iconSize" :icon="isProductChildVisible(product, p.id, p) ? iconCollapse : iconExpand" />
                            </div>
                            <div style="padding: 2px 8px 0 0">
                                {{ shortNameRenderer(p) }}
                            </div>
                        </div>
                    </td>
                    <slot name="row" :item="product" :activeFields="activeFields">
                        <td v-for="field in activeFields" :class="field.tdClass">
                            <slot :name="'cell-' + field.key" :item="p" :field="field">
                                <div v-html="renderField(field, p)"></div>
                            </slot>
                        </td>
                    </slot>
                </tr>
                <slot
                    name="last-row"
                    :item="product"
                    :isVisible="isLastRowVisible(product)"
                    :colCount="activeFields.length + 1"
                    :isSearchActive="isSearchActive"
                />
            </tbody>
        </table>
    </div>
</template>
<script>
import { BCardText, BTable, BCardTitle, BCard, BRow, BCol, BFormGroup, BButton, BCardGroup } from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SearchInput from "@core/components/common/SearchInput";
import YesNoSearch from "@core/components/common/YesNoSearch";

export default {
    components: {
        YesNoSearch,
        SearchInput,
        BCard,
        BCardText,
        BButton,
        BRow,
        BCol,
        BFormGroup,
        BCardGroup,
        BTable,
        BCardTitle,
    },
    directives: {
        Ripple,
    },
    props: {
        search: {
            default: false
        },
        nameField: {
            required: true
        },
        nameRenderer: {
            default() {
                return (item) => item.title;
            }
        },
        shortNameRenderer: {
            default() {
                return (item) => item.title.length > 64 ? item.title.substring(0, 64) + ' ...' : item.title;
            }
        },
        activeFields: {
            type: Array,
            default() {
                return [];
            }
        },
        fields: {
            type: Array,
            default() {
                return [];
            }
        },
        children: {
            type: Array,
            default() {
                return [];
            }
        },
        nested: {
            type: Boolean,
            default: false
        },
        renderItemDeterminer: {
            default() {
                return () => false;
            }
        },
        autoExpand: {
            default: false,
        }
    },
    data() {
        let items = this.initItems(this.children)
        let showExpandColumn = items.filter(p => p.children.length > 0).length > 0;
        return {
            iconSize: '18',
            iconExpand: 'PlusIcon',
            iconCollapse: 'MinusIcon',
            showExpandColumn: showExpandColumn,
            items: items,
            increment: 0,
            isSearchActive: false,
            visible: [],
        }
    },
    methods: {
        needToRender(element) {
            return (this.renderItemDeterminer)(element);
        },
        makeVisible(element) {
            if (!this.visible.includes(element.__id)) {
                this.visible.push(element.__id);
            }
        },
        makeInVisible(element) {
            let key = this.visible.findIndex(id => id === element.__id);
            if (key > -1) {
                this.visible.splice(key, 1);
            }
        },
        isVisible(element) {
            return this.visible.includes(element.__id);
        },
        isLastRowVisible(element) {
            return this.visible.includes(element.__id);
        },
        allVisible() {
            this.visible = this.items.map(i => i.__id);
        },

        filterItem(item) {
            let result = this.fields.concat(this.nameField).filter(f => f.search && f.__searchable).map(f => {
                return f.matcher(item, f.__searchValue);
            });
            if (result.includes(false)) {
                if (result.length === 1 || (result.length > 1 && result.includes(true))) {
                    this.makeInVisible(item);
                }
                return false;
            }
            this.makeVisible(item);
            return true;
        },

        filter(field, value) {
            this.isSearchActive = true;
            field.__searchable = value !== '' && value !== null;
            field.__searchValue = value;

            if (!field.__searchable) {
                if (this.fields.filter(f => f.search).filter((f) => f.__searchable).length === 0) {
                    this.isSearchActive = false;
                    this.allVisible();
                    return;
                }
            }

            this.items.forEach((item) => {
                this.filterItem(item)
                item.flatHierarchy.forEach(this.filterItem.bind(this));
            });
        },

        initItems(items) {
            let i = 0;
            let renderable = items.filter(this.needToRender);
            renderable.forEach((product) => {
                product.expanded = [];
                product.__id = ++i;

                product.flatHierarchy = this.flatten(product, 1);

                product.flatHierarchy.forEach((p) => {
                    p.__id = ++i;
                });

                if (this.autoExpand) {
                    product.expanded = [product.id].concat(product.flatHierarchy.map((p) => p.id));
                }
            });

            return renderable;
        },

        gotoCreateProduct() {
            this.$router.push({name: 'product-new'});
        },

        renderField(field, product) {
            if (field.renderer) {
                return field.renderer(product);
            }

            if (product.hasOwnProperty(field.key)) {
                return product[field.key];
            }

            return '';
        },

        toggleChildren(product, level) {
            let key = product.expanded.findIndex(l => l === level);

            if (key > -1) {
                if (product.id === level) {
                    product.expanded.splice(0);
                    this.$emit('collapsedAll', product.id);
                } else {
                    product.expanded.splice(key, 1);
                    this.$emit('collapsed', product.id, level);
                }
            } else {
                product.expanded.push(level);
                this.$emit('expanded', product.id, level);
            }
        },

        isExpanded(product) {
            return product.hasOwnProperty('expanded') && product.expanded.includes(product.id);
        },

        expand(product, level) {
            let key = product.expanded.findIndex(l => l === level);

            if (key === -1) {
                product.expanded.push(level);
                this.$emit('expanded', product.id, level);
            }
        },

        getItems() {
            return this.items;
        },

        onRowClick(product) {
            this.$emit('click', product);
        },

        getDynamicFieldValue(product, type, name) {
            let p = product[type].find((r) => {
                return r.name === name;
            });

            if (p) {
                return p.value;
            }

            return '';
        },

        isProductChildVisible(product, parent, child) {
            if (!this.search || child === undefined) {
                return product.expanded.includes(parent)
            }

            if (this.search && this.isSearchActive) {
                return this.isVisible(child);
                return true;
            }

            return product.expanded.includes(parent);
        },

        flatten(product, level, parent = null) {
            let result = [];

            product.children.forEach((item) => {
                item.level = level + 1;
                item.parent = parent ? parent.id : product.id;

                result.push(item);
                result = result.concat(this.flatten(item, level + 1, item));
            });

            return result;
        },

        reinitItems() {
            this.items = this.initItems(this.children);
            this.showExpandColumn = this.items.filter(p => p.children.length > 0).length > 0;

            this.$emit('reinitialized', this.items, this.expand);
        }
    },
    watch: {
        children(items) {
            this.items = this.initItems(items);
            this.showExpandColumn = items.filter(p => p.children.length > 0).length > 0;

            this.$emit('reinitialized', this.items, this.expand);
        }
    },
    computed: {
        showArrow() {
            return !this.search || (this.search && !this.isSearchActive);
        }
    },
    created() {
        this.$emit('created', this.items, this.expand);
    }
};
</script>
<style>
.table-responsive.nested-product-overview-container {
    margin: 0;
}
td.children-container {
    padding: 0;
}
</style>
